<template>
  <b-card class="admin-portal-card mb-2">
    <div class="d-flex flex-wrap justify-content-between align-items-start mb-1">
      <div>
        <h4 class="mb-25">Admin Portal</h4>
        <p class="text-muted mb-0">
          Quick access to backoffice tools
          <span v-if="userName"> — {{ userName }}</span>
        </p>
      </div>
      <b-badge v-if="roleLabel" variant="light-primary" class="mt-25">
        {{ roleLabel }}
      </b-badge>
    </div>

    <b-row v-if="visibleItems.length" class="match-height mt-1">
      <b-col
        v-for="item in visibleItems"
        :key="item.route"
        xl="3"
        lg="4"
        md="6"
        cols="12"
        class="mb-1"
      >
        <div class="portal-tile h-100">
          <div class="portal-tile-icon">
            <feather-icon :icon="item.icon" size="22" />
          </div>
          <h5 class="portal-tile-title">{{ item.title }}</h5>
          <p class="portal-tile-desc text-muted">{{ item.description }}</p>
          <div class="portal-tile-actions">
            <b-button
              v-ripple.400="'rgba(115, 103, 240, 0.15)'"
              variant="primary"
              size="sm"
              :to="{ name: item.route }"
            >
              {{ item.cta }}
            </b-button>
            <b-button
              v-if="item.secondaryRoute && item.secondaryCta"
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              size="sm"
              class="ml-50"
              :to="{ name: item.secondaryRoute, query: item.secondaryQuery }"
            >
              {{ item.secondaryCta }}
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>

    <p v-else class="text-muted mb-0 mt-1">
      No admin tools are available for your role. Contact a Super Admin if you need access.
    </p>
  </b-card>
</template>

<script>
import { BCard, BRow, BCol, BButton, BBadge } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { getUserData } from '@/auth/utils'
import { roleLabel as getRoleLabel } from '@/libs/acl/staffAbilities'

export default {
  name: 'DashboardAdminPortal',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
  },
  directives: {
    Ripple,
  },
  computed: {
    userName() {
      const user = getUserData() || {}
      return user.fullName || user.firstName || user.email || ''
    },
    roleLabel() {
      const user = getUserData() || {}
      return user.role ? getRoleLabel(user.role) : ''
    },
    portalItems() {
      return [
        {
          title: 'Staff Users',
          description: 'Add backoffice accounts, assign roles, and manage team access.',
          icon: 'ShieldIcon',
          route: 'staff-users',
          action: 'read',
          subject: 'StaffUsers',
          cta: 'Open',
          secondaryRoute: 'staff-users',
          secondaryQuery: { create: '1' },
          secondaryCta: 'Add staff',
          secondaryAction: 'create',
          secondarySubject: 'StaffUsers',
        },
        {
          title: 'Platform Users',
          description: 'View backers and campaign creators on GivelyCF.',
          icon: 'UsersIcon',
          route: 'users-list',
          action: 'read',
          subject: 'PlatformUsers',
          cta: 'View users',
        },
        {
          title: 'Fundraisers',
          description: 'Manage campaigns, countries, and fundraiser settings.',
          icon: 'LayersIcon',
          route: 'fundraisers-manage',
          action: 'read',
          subject: 'Fundraisers',
          cta: 'Manage',
        },
        {
          title: 'Events',
          description: 'Create and update fundraising events.',
          icon: 'CalendarIcon',
          route: 'events-manage',
          action: 'read',
          subject: 'Events',
          cta: 'Manage',
        },
        {
          title: 'Tickets',
          description: 'Review ticket events, sales, and hide an event from the public.',
          icon: 'TagIcon',
          route: 'tickets-events',
          action: 'read',
          subject: 'TicketEvents',
          cta: 'Open',
        },
        {
          title: 'Transactions',
          description: 'Review donations and ticket purchases.',
          icon: 'CreditCardIcon',
          route: 'transactions-donations',
          action: 'read',
          subject: 'Transactions',
          cta: 'View',
        },
        {
          title: 'Account Settings',
          description: 'Change your password and profile preferences.',
          icon: 'SettingsIcon',
          route: 'pages-account-setting',
          action: 'update',
          subject: 'Settings',
          cta: 'Settings',
        },
      ]
    },
    visibleItems() {
      return this.portalItems
        .filter(item => this.$can(item.action, item.subject))
        .map(item => {
          const showSecondary = item.secondaryCta
            && item.secondaryAction
            && item.secondarySubject
            && this.$can(item.secondaryAction, item.secondarySubject)

          return {
            ...item,
            secondaryRoute: showSecondary ? item.secondaryRoute : null,
            secondaryCta: showSecondary ? item.secondaryCta : null,
            secondaryQuery: showSecondary ? item.secondaryQuery : null,
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-portal-card {
  .card-body {
    padding: 1.25rem 1.5rem;
  }
}

.portal-tile {
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  padding: 1rem 1.1rem;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 4px 18px rgba(115, 103, 240, 0.12);
  }
}

.portal-tile-icon {
  width: 42px;
  height: 42px;
  border-radius: 0.428rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
}

.portal-tile-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.portal-tile-desc {
  font-size: 0.85rem;
  line-height: 1.45;
  min-height: 2.9rem;
  margin-bottom: 0.85rem;
}

.portal-tile-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
</style>
